import { memo, useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { captureMessage } from "@sentry/react";

import LoadingButton from "@mui/lab/LoadingButton";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

import { SubmitState } from "stores/lib";
import {
  useNotificationStore,
  NotificationConfig,
} from "stores/NotificationStore";
import { useSlackStore, SlackProgress } from "stores/SlackStore";

import NotificationConfigItem from "components/NotificationConfig";

// -----------------------------------------------------------------------------

const SlackNotificationConfigPage = memo(
  function SlackNotificationConfigPage() {
    const [notificationStore, notificationActions] = useNotificationStore();
    const [slackStore, slackActions] = useSlackStore();
    const { uuid } = useParams();

    const [basicChannels, setBasicChannels] = useState<string[]>([]);
    const [basicEnable, setBasicEnable] = useState(false);
    const [basicError, setBasicError] = useState(false);

    const [findingChannels, setFindingChannels] = useState<string[]>([]);
    const [findingEnable, setFindingEnable] = useState(false);
    const [findingError, setFindingError] = useState(false);

    const [slackChannels, setSlackChannels] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    useEffect(() => {
      const config = structuredClone(notificationStore.config);
      if (!config || !uuid) {
        return;
      }

      const initialBasicChannels: string[] = [];
      const initialFindingChannels: string[] = [];

      switch (notificationStore.fetchingState) {
        case SubmitState.STARTED:
        case SubmitState.STARTED_AGAIN:
        case SubmitState.SUCCESS:
          config.basic_notification.outputs.slack.destinations.forEach(
            (destination) => {
              if (
                destination.connection === uuid &&
                destination.specific_location
              ) {
                initialBasicChannels.push(
                  destination.specific_location.replace("#", "")
                );
              }
            }
          );
          config.finding_notification.outputs.slack.destinations.forEach(
            (destination) => {
              if (
                destination.connection === uuid &&
                destination.specific_location
              ) {
                initialFindingChannels.push(
                  destination.specific_location.replace("#", "")
                );
              }
            }
          );

          setBasicChannels(initialBasicChannels);
          setBasicEnable(initialBasicChannels.length > 0);
          setFindingChannels(initialFindingChannels);
          setFindingEnable(initialFindingChannels.length > 0);
          break;

        case SubmitState.ERROR:
          captureMessage("Slack notification fetching error");
      }
    }, [notificationStore.fetchingState, notificationStore.config, uuid]);

    // Fetch a fresh config to ensure no stale data
    useEffect(() => {
      notificationActions.fetchConfig();
    }, [notificationActions]);

    const handleBasicChange = (event: any, newValue: string[] | null) => {
      setBasicError(false);
      setBasicChannels(newValue ?? []);
    };

    const handleFindingChange = (event: any, newValue: string[] | null) => {
      setFindingError(false);
      setFindingChannels(newValue ?? []);
    };

    const onSuccess = () => {
      setLoading(false);
      toast.success("Successfully saved the notification settings", {
        position: "bottom-center",
      });
      notificationActions.fetchConfig();
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong saving the notification settings", {
        position: "bottom-center",
      });
    };

    const saveConfig = () => {
      setLoading(true);
      setBasicError(false);
      setFindingError(false);
      let error = false;

      if (!uuid) return;

      const newConfig: Partial<NotificationConfig> = {};

      if (basicEnable) {
        if (basicChannels.length < 1) {
          setBasicError(true);
          error = true;
        } else {
          const destinations = [];
          for (const channel of basicChannels) {
            destinations.push({
              filter: null,
              connection: uuid,
              specific_location: `#${channel}`,
            });
          }

          newConfig.basic_notification = {
            outputs: {
              slack: {
                enabled: true,
                destinations,
              },
            },
          };
        }
      }

      if (findingEnable) {
        if (findingChannels.length < 1) {
          setFindingError(true);
          error = true;
        } else {
          const destinations = [];
          for (const channel of findingChannels) {
            destinations.push({
              filter: null,
              connection: uuid,
              specific_location: `#${channel}`,
            });
          }

          newConfig.finding_notification = {
            outputs: {
              slack: {
                enabled: true,
                destinations,
              },
            },
          };
        }
      }

      if (error) {
        setLoading(false);
        return;
      }

      const config =
        newConfig.basic_notification ?? newConfig.finding_notification
          ? newConfig
          : null;

      notificationActions.updateConfig(config, uuid, onSuccess, onError);
    };

    const getChannels = useCallback(() => {
      if (!uuid || !slackStore.channels[uuid]) {
        setSlackChannels([]);
        return;
      }

      const channels: string[] = [];
      slackStore.channels[uuid].forEach((channel) => {
        const channelName = channel[0];
        const channelId = channel[1];
        channels.push(`${channelName} (${channelId})`);
      });
      setSlackChannels(channels);
    }, [slackStore.channels, uuid]);

    //
    useEffect(() => {
      switch (slackStore.listProgress) {
        case undefined:
        case SlackProgress.Unknown:
          slackActions.listChannels();
          break;
        case SlackProgress.Submitting:
          break;
        case SlackProgress.Success:
          getChannels();
          break;
        case SlackProgress.Error:
          captureMessage("Slack notification listing channel error");
      }
    }, [slackStore.listProgress, getChannels, slackActions]);

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
            Slack
          </Typography>
          <Typography variant="body1">
            Looking to fine-tune your Slack notifications? You&apos;ve come to
            the right place! With our notification configuration page, you can
            tailor your Slack experience to better suit your needs. Once
            you&apos;re happy with your settings, simply hit the
            &quot;Save&quot; button and you&apos;re all set!
          </Typography>
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }} />

        <Stack spacing={2}>
          <NotificationConfigItem
            enabled={basicEnable}
            label={"Basic Notifications"}
            description={
              "Basic Notifications include all things related to your account lifecycle (such as billing) and other common platform features"
            }
            setEnabled={setBasicEnable}
          >
            {basicEnable ? (
              <>
                <Typography>
                  Choose the <strong>Channel(s)</strong> you would like to send
                  basic notifications to.
                </Typography>

                <FormControl required fullWidth error={basicError}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    id="basic-channel"
                    options={slackChannels}
                    getOptionLabel={(option) => (option ? `#${option}` : "")}
                    value={basicChannels}
                    onChange={handleBasicChange}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        #{option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        required
                        error={basicError}
                        {...params}
                        label="Channels"
                      />
                    )}
                  />
                  {basicError ? (
                    <FormHelperText>Required</FormHelperText>
                  ) : null}
                </FormControl>
                <span style={{ fontWeight: "bold" }}>
                  {" "}
                  You must invite the ThreatKey bot to the selected channels to
                  receive notifications.
                  <br /> Complete this step by sending <code>
                    @threatkey
                  </code>{" "}
                  in the channels you wish to receive notifications in, and
                  clicking the &quot;Invite&quot; button.
                </span>
              </>
            ) : null}
          </NotificationConfigItem>

          <NotificationConfigItem
            enabled={findingEnable}
            label={"Finding Notifications"}
            description={
              "Notifications for security findings can be configured in a variety of ways, including the ability to receive a real-time notification whenever a new security finding is generated. We highly recommend that you configure your notifications to include security findings. This way, you will be alerted as soon as possible if there are any potential threats to your company that ThreatKey has detected."
            }
            setEnabled={setFindingEnable}
          >
            {findingEnable ? (
              <>
                <Typography>
                  Choose the <strong>Channel(s)</strong> you would like to send
                  finding notifications to.
                </Typography>

                <FormControl required fullWidth error={findingError}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    id="finding-channel"
                    options={slackChannels}
                    getOptionLabel={(option) => (option ? `#${option}` : "")}
                    value={findingChannels}
                    onChange={handleFindingChange}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        #{option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        required
                        error={basicError}
                        {...params}
                        label="Channels"
                      />
                    )}
                  />
                  {findingError ? (
                    <FormHelperText>Required</FormHelperText>
                  ) : null}
                </FormControl>
                <span style={{ fontWeight: "bold" }}>
                  {" "}
                  You must invite the ThreatKey bot to the selected channels to
                  receive notifications.
                  <br /> Complete this step by sending <code>
                    @threatkey
                  </code>{" "}
                  in the channels you wish to receive notifications in, and
                  clicking the &quot;Invite&quot; button.
                </span>
              </>
            ) : null}
          </NotificationConfigItem>

          <Stack sx={{ mt: 2 }} spacing={2} direction="row">
            <LoadingButton
              variant="contained"
              onClick={saveConfig}
              loading={loading}
            >
              Save
            </LoadingButton>
            <Link
              to="/environment/destinations"
              style={{ textDecoration: "none" }}
            >
              <Button variant="text">Cancel</Button>
            </Link>
          </Stack>
        </Stack>
      </>
    );
  }
);

export default SlackNotificationConfigPage;
